import React, { useEffect, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'

import Navbar from './Navbar'
import CSVUploader from './CSVUploader'
import BrandingForm from './BrandingForm'
import ManageUsersModal from './ManageUsersModal'

import {
  fetchAdminStats,
  fetchFeedbacks,
  fetchFaqDetail,
  updateFaq,
  deleteFaq,
  deleteFeedback,
  getCurrentUser,
  logoutUser,
} from '../utils/api'

function MetricCard({ title, value, icon }) {
  return (
    <div className="col-sm-6 col-md-3 mb-3">
      <div className="card text-center h-100">
        <div className="card-body d-flex flex-column justify-content-center">
          <h6 className="card-subtitle mb-2 text-muted">
            {icon && <span className="me-1">{icon}</span>}
            {title}
          </h6>
          <p className="display-6 mb-0">{value}</p>
        </div>
      </div>
    </div>
  )
}

export default function AdminDashboard({ branding: companyBranding, onBrandingChange }) {
  const navigate = useNavigate()

  const [user, setUser] = useState(null)
  const [loadingUser, setLoadingUser] = useState(true)
  useEffect(() => {
    ;(async () => {
      try {
        const u = await getCurrentUser()
        setUser(u)
      } catch {
        navigate('/')
      } finally {
        setLoadingUser(false)
      }
    })()
  }, [navigate])

  // Admin stats
  const [stats, setStats] = useState([])
  const [loadingStats, setLoadingStats] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const PAGE_SIZE = 15
  const [currentPage, setCurrentPage] = useState(1)

  const loadStats = async () => {
    setLoadingStats(true)
    try {
      const data = await fetchAdminStats(searchTerm)
      setStats(data)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoadingStats(false)
    }
  }

  useEffect(() => {
    if (!user) return
    ;(async () => {
      setLoadingStats(true)
      try {
        const data = await fetchAdminStats()
        setStats(data)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoadingStats(false)
      }
    })()
  }, [user])

  // Sorting
  const [sortKey, setSortKey] = useState('usage_count')
  const [sortDir, setSortDir] = useState('desc')
  const toggleSort = key => {
    if (key === sortKey) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))
    else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedFaqs = useMemo(
    () =>
      [...stats].sort(
        (a, b) => (b[sortKey] - a[sortKey]) * (sortDir === 'desc' ? 1 : -1)
      ),
    [stats, sortKey, sortDir]
  )
  const topByUsage = useMemo(
    () => [...stats].sort((a, b) => b.usage_count - a.usage_count).slice(0, 5),
    [stats]
  )

  // Filter by searchTerm
  const filteredFaqs = useMemo(() => {
    if (!searchTerm) return sortedFaqs
    const lower = searchTerm.toLowerCase()
    return sortedFaqs.filter(f =>
      f.question.toLowerCase().includes(lower)
    )
  }, [sortedFaqs, searchTerm])

  // Reset to page 1 on new filter or sort
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, sortKey, sortDir])

  // Paginate
  const totalPages = Math.ceil(filteredFaqs.length / PAGE_SIZE)
  const paginatedFaqs = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return filteredFaqs.slice(start, start + PAGE_SIZE)
  }, [filteredFaqs, currentPage])

  // Modal & feedback state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalFaq, setModalFaq] = useState(null)
  const [feedbacks, setFeedbacks] = useState([])
  const [checkedIds, setCheckedIds] = useState(new Set())
  const [saving, setSaving] = useState(false)
  const [feedbackListOpen, setFeedbackListOpen] = useState(false)
  const [showUploader, setShowUploader] = useState(false)
  const [showBrandingModal, setShowBrandingModal] = useState(false)
  const [showUsersModal, setShowUsersModal] = useState(false)

  const openModal = async faq => {
    let detail
    try {
      detail = await fetchFaqDetail(faq.id)
    } catch {
      detail = faq
    }
    setModalFaq(detail)
    setModalOpen(true)

    try {
      const all = await fetchFeedbacks(faq.id)
      setFeedbacks(all.filter(fb => fb.comment && fb.comment.trim() !== ''))
    } catch {
      setFeedbacks([])
    }
    setCheckedIds(new Set())
  }

  const saveFaq = async () => {
    if (!modalFaq) return
    setSaving(true)
    try {
      await updateFaq(modalFaq.id, {
        question: modalFaq.question,
        answer: modalFaq.answer,
        language: modalFaq.language,
        country: modalFaq.country,
        category: modalFaq.category,
      })
      await loadStats()
      setModalOpen(false)
      setCheckedIds(new Set())
    } catch (err) {
      alert('Save failed: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!modalFaq || !window.confirm('Delete this FAQ?')) return
    try {
      await deleteFaq(modalFaq.id)
      setStats(s => s.filter(f => f.id !== modalFaq.id))
      setModalOpen(false)
      setCheckedIds(new Set())
    } catch (err) {
      alert('Delete failed: ' + err.message)
    }
  }

  const toggleCheck = id => {
    setCheckedIds(prev => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }

  const confirmRead = async () => {
    if (!modalFaq) return
    const toDelete = feedbacks.filter(fb => checkedIds.has(fb.id))
    try {
      await Promise.all(
        toDelete.map(fb => deleteFeedback(modalFaq.id, fb.id))
      )
      const all = await fetchFeedbacks(modalFaq.id)
      setFeedbacks(all.filter(fb => fb.comment?.trim()))
      await loadStats()
      setCheckedIds(new Set())
      setFeedbackListOpen(false)
    } catch (err) {
      console.error(err)
      alert('Failed to remove selected feedback.')
    }
  }

  if (loadingUser) return <p>Loading user…</p>
  if (loadingStats) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading dashboard…</span>
        </Spinner>
      </div>
    )
  }

  if (error) return <p className="text-danger">Error: {error}</p>

  return (
    <div className="admin-dashboard">
      <Navbar
        user={user}
        logoUrl={companyBranding?.logo_url}
        onShowUploader={() => setShowUploader(true)}
        onLogout={async () => {
          await logoutUser()
          navigate('/')
        }}
      />

      <CSVUploader
        show={showUploader}
        onHide={() => setShowUploader(false)}
      />

      <div
        className="container py-4"
        style={{ filter: feedbackListOpen ? 'blur(5px)' : 'none' }}
      >
        {/* Header */}
        <div className="admin-dashboard_header d-flex justify-content-between align-items-center">
          <h2>Admin Dashboard</h2>
          <div>
            <Button
              variant="outline-secondary"
              className="me-2"
              onClick={() => setShowUsersModal(true)}
            >
              Manage Users
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => setShowBrandingModal(true)}
            >
              Edit Branding
            </Button>
          </div>
        </div>

        {/* Metrics */}
        <div className="admin-dashboard_metrics row mb-4">
          <MetricCard title="Total FAQs" value={stats.length} />
          <MetricCard
            title="Upvotes"
            value={stats.reduce((a, f) => a + f.upvote_count, 0)}
            icon="👍"
          />
          <MetricCard
            title="Downvotes"
            value={stats.reduce((a, f) => a + f.downvote_count, 0)}
            icon="👎"
          />
          <MetricCard
            title="Total Usage"
            value={stats.reduce((a, f) => a + f.usage_count, 0)}
          />
        </div>

        {/* Top 5 */}
        <div className="admin-dashboard_top5 mt-5">
          <h4>Top 5 FAQs by Usage</h4>
          <ul className="admin-dashboard_top5-list list-group mb-4">
            {topByUsage.map(faq => (
              <li
                key={faq.id}
                className="admin-dashboard_top5-item list-group-item d-flex justify-content-between"
                onClick={() => openModal(faq)}
                style={{ cursor: 'pointer' }}
              >
                {faq.question}
                <span className="badge bg-primary">{faq.usage_count}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Search + Table + Pagination */}
        <h4 className="admin-dashboard_allfaqs-title mt-5">All FAQs</h4>
        <div className="admin-dashboard_search row mb-3">
          <div className="col-md-4">
            <Form.Control
              className="admin-dashboard_search-input"
              type="text"
              placeholder="🔍 Search FAQs…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <table className="admin-dashboard_table table table-striped">
          <thead>
            <tr>
              <th onClick={() => toggleSort('question')} style={{ cursor: 'pointer' }}>
                Question {sortKey === 'question' && (sortDir === 'desc' ? '▼' : '▲')}
              </th>
              <th className="text-center" onClick={() => toggleSort('upvote_count')} style={{ cursor: 'pointer' }}>
                👍 Upvotes {sortKey === 'upvote_count' && (sortDir === 'desc' ? '▼' : '▲')}
              </th>
              <th className="text-center" onClick={() => toggleSort('downvote_count')} style={{ cursor: 'pointer' }}>
                👎 Downvotes {sortKey === 'downvote_count' && (sortDir === 'desc' ? '▼' : '▲')}
              </th>
              <th className="text-center" onClick={() => toggleSort('feedback_count')} style={{ cursor: 'pointer' }}>
                Feedback {sortKey === 'feedback_count' && (sortDir === 'desc' ? '▼' : '▲')}
              </th>
              <th className="text-center" onClick={() => toggleSort('usage_count')} style={{ cursor: 'pointer' }}>
                Usage {sortKey === 'usage_count' && (sortDir === 'desc' ? '▼' : '▲')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedFaqs.map(faq => (
              <tr key={faq.id} onClick={() => openModal(faq)} style={{ cursor: 'pointer' }}>
                <td>{faq.question}</td>
                <td className="text-center">{faq.upvote_count}</td>
                <td className="text-center">{faq.downvote_count}</td>
                <td className="text-center">{faq.feedback_count}</td>
                <td className="text-center">{faq.usage_count}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <nav className="admin-dashboard_pagination-nav">
            <ul className="admin-dashboard_pagination pagination justify-content-center">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                  Next
                </button>
              </li>
            </ul>
          </nav>
        )}

        {/* Back */}
        <div className="admin-dashboard_back mt-4">
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            ← Back to FAQs
          </Button>
        </div>
      </div>

      {/* FAQ Detail Modal */}
      {modalFaq && (
        <Modal className="admin-dashboard_details-modal" show={modalOpen} onHide={() => setModalOpen(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>FAQ Details</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <h6
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setFeedbackListOpen(true)}
            >
              User Feedback ({feedbacks.length})
            </h6>
            <hr />
            <h6>Edit FAQ</h6>
            <div className="mb-3">
              <label htmlFor="modal-question" className="form-label">Question</label>
              <input
                id="modal-question"
                className="form-control"
                value={modalFaq.question}
                onChange={e => setModalFaq(f => ({ ...f, question: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="modal-answer" className="form-label">Answer</label>
              <textarea
                id="modal-answer"
                rows={3}
                className="form-control"
                value={modalFaq.answer}
                onChange={e => setModalFaq(f => ({ ...f, answer: e.target.value }))}
              />
            </div>
            <div className="row">
              <div className="col mb-3">
                <label htmlFor="modal-language" className="form-label">Language</label>
                <input
                  id="modal-language"
                  className="form-control"
                  value={modalFaq.language}
                  onChange={e => setModalFaq(f => ({ ...f, language: e.target.value }))}
                />
              </div>
              <div className="col mb-3">
                <label htmlFor="modal-country" className="form-label">Country</label>
                <input
                  id="modal-country"
                  className="form-control"
                  value={modalFaq.country}
                  onChange={e => setModalFaq(f => ({ ...f, country: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label htmlFor="modal-category" className="form-label">Category</label>
              <input
                id="modal-category"
                className="form-control"
                value={modalFaq.category}
                onChange={e => setModalFaq(f => ({ ...f, category: e.target.value }))}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="danger" onClick={handleDelete} disabled={saving}>Delete</Button>
            <Button variant="secondary" onClick={() => setModalOpen(false)} disabled={saving}>Close</Button>
            <Button variant="primary" onClick={saveFaq} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Feedback List Modal */}
      <Modal
        className="admin-dashboard_feedback-modal"
        show={feedbackListOpen}
        onHide={() => setFeedbackListOpen(false)}
        centered
        backdropClassName="feedback-backdrop"
        contentClassName="feedback-modal-content"
      >
        <Modal.Header closeButton>
          <Modal.Title>All Feedback for "{modalFaq?.question}"</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {feedbacks.length > 0 ? (
            feedbacks.map(fb => (
              <div
                key={fb.id}
                className="feedback-item d-flex justify-content-between align-items-start border p-3 mb-2"
              >
                <div className="feedback-content">
                  <small className="text-muted d-block">
                    {new Date(fb.created_at).toLocaleString()}
                  </small>
                  <p className="mb-1">{fb.comment || <em>No comment</em>}</p>
                  <small className="text-muted">{fb.user_email}</small>
                </div>
                <Form.Check
                  type="checkbox"
                  id={`fb-${fb.id}`}
                  checked={checkedIds.has(fb.id)}
                  onChange={() => toggleCheck(fb.id)}
                  className="feedback-checkbox"
                />
              </div>
            ))
          ) : (
            <p className="text-muted">No feedback at all.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFeedbackListOpen(false)}>Close</Button>
          <Button variant="primary" onClick={confirmRead} disabled={checkedIds.size === 0 || saving}>
            {saving ? 'Updating…' : `I've read all feedback`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Branding Modal */}
      <Modal
        className="admin-dashboard_branding-modal"
        show={showBrandingModal}
        onHide={() => setShowBrandingModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Branding & Appearance</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BrandingForm
            initialSettings={companyBranding}
            onSave={onBrandingChange}
          />
        </Modal.Body>
      </Modal>

      {/* Manage Users */}
      <ManageUsersModal
        className="admin-dashboard_users-modal"
        show={showUsersModal}
        onHide={() => setShowUsersModal(false)}
      />
    </div>
  )
}
